import * as ImageSize from "utils/ImageSize";
import generateImageLink from "utils/generateImageLink";
import React, { useCallback, useContext, useRef } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import ImageAspectRatioCacheContext from "context/ImageAspectRatioCacheContext";
import styles from "./image/Image.css";
import { useIsMobile } from "../hooks/useIsMobile";

const Image = React.forwardRef(
  (
    {
      className,
      onLoad,
      cachedDataUrl,
      src,
      dpr,
      lossless,
      size,
      ...passthroughProps
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const imageRef = useRef();
    const { cacheImageAspectRatio } = useContext(ImageAspectRatioCacheContext);

    const imageUrl = generateImageLink(src, {
      dpr,
      isMobile,
      lossless,
      size,
    });

    function refCallback(instance) {
      imageRef.current = instance;

      if (typeof ref === "function") {
        ref(instance);
      } else if (ref) {
        ref.current = instance;
      }
    }

    const handleLoad = useCallback(
      (event) => {
        cacheImageAspectRatio(
          imageUrl,
          imageRef.current.naturalWidth / imageRef.current.naturalHeight,
        );

        if (onLoad) {
          onLoad(event);
        }
      },
      [cacheImageAspectRatio, imageUrl, onLoad],
    );

    return (
      <img
        {...passthroughProps}
        alt=""
        className={classnames(styles.image, className)}
        draggable={false}
        onLoad={handleLoad}
        ref={refCallback}
        src={cachedDataUrl || imageUrl}
      />
    );
  },
);

export default Image;

Image.defaultProps = {
  size: ImageSize.LARGE,
};

Image.propTypes = {
  cachedDataUrl: PropTypes.string,
  className: PropTypes.string,
  dpr: PropTypes.number,
  lossless: PropTypes.bool,
  onLoad: PropTypes.func,
  size: PropTypes.oneOf(Object.values(ImageSize)),
  src: PropTypes.string.isRequired,
};
